import { UploadFile } from '../../types/image'
import { X, Check, AlertCircle, Loader2 } from 'lucide-react'
import { formatFileSize } from '../../utils/formatting'

interface UploadProgressProps {
  uploads: UploadFile[]
  onRemove?: (id: string) => void
}

export const UploadProgress: React.FC<UploadProgressProps> = ({ uploads, onRemove }) => {
  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5 text-green-500" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'uploading':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
    }
  }

  const getStatusText = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return 'Uploaded'
      case 'error':
        return 'Failed'
      case 'uploading':
        return 'Uploading...'
      case 'processing':
        return 'Processing...'
      default:
        return 'Waiting'
    }
  }

  return (
    <div className="space-y-3">
      {uploads.map((upload) => (
        <div
          key={upload.id}
          className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
            upload.status === 'error'
              ? 'bg-red-50 border-red-100'
              : upload.status === 'completed'
              ? 'bg-green-50 border-green-100'
              : upload.status === 'uploading'
              ? 'bg-blue-50 border-blue-100'
              : 'bg-gray-50 border-gray-100'
          }`}
        >
          {/* File Icon */}
          <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <span className="text-xs font-medium text-gray-400 uppercase">
              {upload.file.name.split('.').pop()?.slice(0, 3)}
            </span>
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">
                {upload.file.name}
              </p>
              <span className="text-xs text-gray-400">
                ({formatFileSize(upload.file.size)})
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-2">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    upload.status === 'error'
                      ? 'bg-red-500'
                      : upload.status === 'completed'
                      ? 'bg-green-500'
                      : 'bg-blue-500'
                  }`}
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            </div>
            
            {/* Status */}
            <div className="flex items-center gap-2 mt-1.5">
              {getStatusIcon(upload.status)}
              <span className={`text-xs font-medium ${
                upload.status === 'error'
                  ? 'text-red-600'
                  : upload.status === 'completed'
                  ? 'text-green-600'
                  : upload.status === 'uploading'
                  ? 'text-blue-600'
                  : 'text-gray-500'
              }`}>
                {getStatusText(upload.status)}
              </span>
              {upload.status === 'uploading' && (
                <span className="text-xs text-gray-400">{upload.progress}%</span>
              )}
            </div>
            
            {/* Error Message */}
            {upload.error && (
              <p className="text-xs text-red-600 mt-1">{upload.error}</p>
            )}
          </div>

          {/* Remove Button */}
          {onRemove && upload.status !== 'uploading' && (
            <button
              onClick={() => onRemove(upload.id)}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}